import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { useGenericSearch } from '@/hooks/useGenericSearch';
import { ApiKey } from '@/types';
import { useMutation } from '@/utils/api';

/**
 * Extended mutation options with toast control
 */
interface MutationToastOptions {
  /** Suppress default success toast */
  silent?: boolean;
  /** Custom success message */
  successMessage?: string;
}

/**
 * Create API key with automatic success toast
 *
 * Default behavior: Shows "API key created successfully" toast
 * Override: Pass { silent: true } in mutate options to suppress
 *
 * @example
 * // Standard usage (automatic toast)
 * const { mutate } = useCreateApiKey();
 * mutate({ name: 'My Key' });
 *
 * @example
 * // Custom messaging (suppress hook toast)
 * const { mutateAsync } = useCreateApiKey();
 * await mutateAsync(
 *   { name: 'My Key' },
 *   {
 *     silent: true,
 *     onSuccess: () => toast.success('Custom message with details')
 *   }
 * );
 */
export function useCreateApiKey() {
  // Pinned to self, matching the key list. Sending the impersonated account
  // filed the key under that tenant, where the list could never show it.
  const axios = useAxios({ doNotImpersonate: true });
  const { invalidate } = useGenericSearch(
    {
      query: '#key',
    },
    { enabled: false }
  );
  return useMutation<ApiKey, Error, Partial<ApiKey>>({
    mutationFn: async (payload: Partial<ApiKey>) => {
      const { data } = await axios.post('/key', payload);
      return data as ApiKey;
    },
    onSuccess: (data, variables, context) => {
      invalidate();

      // Check if toast is suppressed via context
      const options = context as MutationToastOptions | undefined;
      if (!options?.silent) {
        toast.success(
          options?.successMessage || 'API key created successfully'
        );
      }
    },
    defaultErrorMessage: 'Failed to create API key',
  });
}

/**
 * Delete API key with automatic success toast
 *
 * Default behavior: Shows "API key deleted successfully" toast
 * Cannot be suppressed (critical operations should always notify user)
 *
 * @example
 * const { mutate: deleteKey } = useDeleteApiKey();
 * deleteKey({ key: 'key-id' }); // ✅ Hook shows toast automatically
 */
export function useDeleteApiKey() {
  // Same pin as create: the listed keys are the caller's own.
  const axios = useAxios({ doNotImpersonate: true });
  const { invalidate } = useGenericSearch(
    {
      query: '#key',
    },
    { enabled: false }
  );
  return useMutation<void, Error, { key: string }>({
    mutationFn: async (payload: { key: string }) => {
      const { data } = await axios.delete('/key', { data: payload });
      return data;
    },
    onSuccess: () => {
      invalidate();
      // Critical operations always show toast (no silent option)
      toast.success('API key deleted successfully');
    },
    defaultErrorMessage: 'Failed to delete API key',
  });
}
